import React from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;

export default function Contact() {
  const [form, setForm] = React.useState({
    name: "", email: "", subject: "", message: "", honeypot: "",
  });
  const [status, setStatus] = React.useState("idle");  // idle | sending | sent | error
  const [error, setError] = React.useState(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 3) {
      setError("Name, email, and a message (at least a few words) are required.");
      return;
    }
    setStatus("sending"); setError(null);
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("sent");
      setForm({ name: "", email: "", subject: "", message: "", honeypot: "" });
    } catch (e) {
      setStatus("error");
      setError(e?.response?.data?.error || "Couldn't send the message — please try again.");
    }
  };

  return (
    <div data-testid="public-contact" className="max-w-5xl">
      <div className="overline-warm mb-4">Contact</div>
      <h1 className="display-lg mb-6">Write to Elle.</h1>

      <p className="prose-warm mb-8">
        The courier takes everything and runs it past an abuse filter before it
        reaches Elle. Genuine correspondence — agents, editors, readers with
        questions — lands promptly. Abuse does not.
      </p>

      <p className="prose-warm mb-10" data-testid="contact-mailto">
        Or write directly: <a href="mailto:Elle@ellestuartauthor.com"
           className="underline"
           style={{ color: "var(--warm-amber)" }}>
          Elle@ellestuartauthor.com
        </a>
      </p>

      <div className="contact-layout grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] items-start">
        <div className="contact-main order-2 lg:order-1">
          {status === "sent" ? (
            <div className="contact-success" data-testid="contact-success">
              <div className="overline-warm mb-2">Message received</div>
              <p className="prose-warm">
                Thank you. Elle reads messages most mornings. If your message needs
                a reply, one will come — not always quickly, but always considered.
              </p>
              <button onClick={() => setStatus("idle")}
                      className="btn-warm-ghost mt-6"
                      data-testid="contact-send-another">
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="contact-form space-y-5"
                  data-testid="contact-form">
              <div>
                <label className="overline-warm mb-2 block" htmlFor="c-name">Your name</label>
                <input id="c-name" type="text" value={form.name}
                       onChange={update("name")} required
                       data-testid="contact-name"
                       className="input-warm"/>
              </div>
              <div>
                <label className="overline-warm mb-2 block" htmlFor="c-email">Email</label>
                <input id="c-email" type="email" value={form.email}
                       onChange={update("email")} required
                       data-testid="contact-email"
                       className="input-warm"/>
              </div>
              <div>
                <label className="overline-warm mb-2 block" htmlFor="c-subject">Subject <span className="muted">(optional)</span></label>
                <input id="c-subject" type="text" value={form.subject}
                       onChange={update("subject")}
                       data-testid="contact-subject"
                       className="input-warm"/>
              </div>
              <div>
                <label className="overline-warm mb-2 block" htmlFor="c-message">Message</label>
                <textarea id="c-message" rows={8} value={form.message}
                          onChange={update("message")} required
                          data-testid="contact-message"
                          className="input-warm"/>
              </div>
              {/* Honeypot — hidden from real humans */}
              <input type="text" name="company" tabIndex={-1} autoComplete="off"
                     value={form.honeypot} onChange={update("honeypot")}
                     style={{position:"absolute", left:"-9999px", width:"1px", height:"1px"}}
                     aria-hidden="true"/>
              {error && <p className="contact-error" data-testid="contact-error">{error}</p>}
              <button type="submit" disabled={status === "sending"}
                      className="btn-warm-primary"
                      data-testid="contact-submit">
                {status === "sending" ? "Sending…" : "Send message"}
              </button>
            </form>
          )}
        </div>

        <figure className="contact-courier contact-courier--beside order-1 lg:order-2"
                data-testid="contact-courier-img">
          <img src="/images/courier-post-transparent.png"
               alt="A weathered wooden post box labelled 'Inter-Grounds Courier Service', encircled by a glowing protective mesh; a satchel and traveller's armour rest beside it."
               className="contact-courier-img"/>
          <figcaption className="contact-courier-caption">
            All messages pass through the shield.
          </figcaption>
        </figure>
      </div>
    </div>
  );
}
